import { capitalize } from 'es-toolkit/compat';
import { useMemo } from 'react';

import { type GuidDto, type InvoiceOrCreditDto, type PaymentDto } from 'app/api/apiClient/generated/types';
import { type Bill } from 'app/api/apiClient/settlement/types';
import { useGetBillsPayors } from 'app/api/hooks/paperwork/v1/bills';
import { billStateTranslation, paymentStatusTranslation } from 'app/api/i18n';
import { type GlobalSearchDataSection } from 'app/components/complex/GlobalSearch/GlobalSearchResults';
import { billParams } from 'app/constants/param-names';
import { PayoutStatusId } from 'app/constants/payment';
import { payeeRoutes, routes } from 'app/constants/routes';
import { payeePaperworkStatusConfig, paymentStatusColors } from 'app/constants/status';
import { useHasMultiCurrency } from 'app/hooks/payee/useHasMultiCurrency';
import {
  defaultPayeeInvoicesQueryParams,
  getInvoiceOrCreditNoteStatus,
  type PayeeInvoicesQueryParamsWithView,
  renderPayeeInvoiceAmount,
} from 'app/pages/payee/Invoices/helpers';
import { defaultPayeePaperworkQueryParams, renderPayeePaperworkPayorName } from 'app/pages/payee/Paperwork/helpers';
import { type PayeePaperworkQueryParams } from 'app/pages/payee/Paperwork/hooks';
import PayeePaperworkCurrencyText from 'app/pages/payee/Paperwork/PayeePaperworkCurrencyText';
import { defaultPayeePaymentsQueryParams, renderPayeePaymentAmount } from 'app/pages/payee/Payments/helpers';
import { type PayeePaymentsQueryParams } from 'app/pages/payee/Payments/hooks';
import { indexBy } from 'app/utils/lib/array';
import { stringifyQueryParams } from 'app/utils/lib/string';

import { type PayeeGlobalSearchResult } from './helpers';
import PayeeGlobalSearchLabel from './PayeeGlobalSearchLabel';

// eslint-disable-next-line @stylistic/max-len, max-params
type GetGlobalSearchSection<TData> = (data: TData[], searchString: string, hasMultiCurrency: boolean, payorNames: Record<string, GuidDto>) => GlobalSearchDataSection;

const PaymentsLabel = <PayeeGlobalSearchLabel type="payments" />;
const InvoicesLabel = <PayeeGlobalSearchLabel type="invoices" />;
const PaperworkLabel = <PayeeGlobalSearchLabel type="paperwork" />;

// eslint-disable-next-line @stylistic/max-len
const getPaymentsSection: GetGlobalSearchSection<PaymentDto> = (payments, searchString, hasMultiCurrency): GlobalSearchDataSection => {
  const params: PayeePaymentsQueryParams = {
    ...defaultPayeePaymentsQueryParams,
    q: searchString,
  };

  return {
    data: payments.map(payment => ({
      bottomLeft: payment.payor?.name ?? '',
      id: payment.id,
      pillProps: {
        label: paymentStatusTranslation[payment.status.id as PayoutStatusId] ?? payment.status.name,
        statusColor: paymentStatusColors[payment.status.id as PayoutStatusId] ?? 'blue',
      },
      to: `/${routes.payee}/${payeeRoutes.payments}/${payment.id}`,
      topLeft: payment.bankAccount.name,
      topRight: renderPayeePaymentAmount(payment, hasMultiCurrency),
    })),
    id: 'payments',
    label: PaymentsLabel,
    to: `/${routes.payee}/${payeeRoutes.payments}?${stringifyQueryParams({ ...params })}`,
  };
};

// eslint-disable-next-line @stylistic/max-len
const getInvoicesSection: GetGlobalSearchSection<InvoiceOrCreditDto> = (invoices, searchString, hasMultiCurrency): GlobalSearchDataSection => {
  const params: PayeeInvoicesQueryParamsWithView = {
    ...defaultPayeeInvoicesQueryParams,
    q: searchString,
  };

  return {
    data: invoices.map(invoice => {
      const { statusConfig, statusName } = getInvoiceOrCreditNoteStatus(invoice);

      return ({
        bottomLeft: invoice.payor?.name ?? '',
        id: invoice.id,
        pillProps: {
          label: statusName ?? '',
          statusColor: statusConfig?.pillColor ?? 'blue',
        },
        to: `/${routes.payee}/${payeeRoutes.invoices}/${invoice.id}`,
        // eslint-disable-next-line @stylistic/max-len
        topLeft: `${invoice.referenceNo ? `Ref ${invoice.referenceNo} ` : ''} ${invoice.invoiceNo ? `Inv ${invoice.invoiceNo}` : ''}`.trim(),
        topRight: renderPayeeInvoiceAmount(invoice, hasMultiCurrency),
      });
    }),
    id: 'invoices',
    label: InvoicesLabel,
    to: `/${routes.payee}/${payeeRoutes.invoices}?${stringifyQueryParams({ ...params })}`,
  };
};

// eslint-disable-next-line @stylistic/max-len, max-params
const getPaperworkSection: GetGlobalSearchSection<Bill> = (bills, searchString, hasMultiCurrency, payorNames): GlobalSearchDataSection => {
  const params: PayeePaperworkQueryParams = {
    ...defaultPayeePaperworkQueryParams,
    [billParams.search]: searchString,
  };

  return {
    data: bills.map(bill => ({
      bottomLeft: renderPayeePaperworkPayorName(payorNames[bill.payorId]?.name),
      id: bill.id,
      pillProps: {
        label: capitalize(billStateTranslation[bill.state]),
        statusColor: payeePaperworkStatusConfig[bill.state]?.pillColor ?? 'blue',
      },
      to: `/${routes.payee}/${payeeRoutes.paperwork}/${bill.id}`,
      // eslint-disable-next-line @stylistic/max-len
      topLeft: `${bill.loadNumber ? `Load ${bill.loadNumber} ` : ''} ${bill.invoiceNumber ? `Inv ${bill.invoiceNumber}` : ''}`.trim(),
      topRight: (
        <PayeePaperworkCurrencyText
          bill={bill}
          hasMultiCurrency={hasMultiCurrency}
        />
      ),
    })),
    id: 'paperwork',
    label: PaperworkLabel,
    to: `/${routes.payee}/${payeeRoutes.paperwork}?${stringifyQueryParams({ ...params })}`,
  };
};

// eslint-disable-next-line @stylistic/max-len
export const useMapPayeeGlobalSearchResult = (result: Partial<PayeeGlobalSearchResult> | undefined, searchString: string): GlobalSearchDataSection[] | null => {
  const { hasMultiCurrency } = useHasMultiCurrency();

  const {
    data: payorNameData,
  } = useGetBillsPayors();
  const payorNames = useMemo(() =>
    indexBy(payorNameData ?? [], payor => payor.id),
  [payorNameData]);

  if (!result || !searchString) {
    return null;
  }

  return [
    result.payments?.length && getPaymentsSection(result.payments, searchString, hasMultiCurrency, payorNames),
    result.invoices?.length && getInvoicesSection(result.invoices, searchString, hasMultiCurrency, payorNames),
    result.bills?.length && getPaperworkSection(result.bills, searchString, hasMultiCurrency, payorNames),
  ].filter((x): x is GlobalSearchDataSection => Boolean(x));
};
